import andrey_png from '@/assets/Andrey.jpg';
import connor_png from '@/assets/Connor.jpg';
import logo from '@/assets/swimhomes_trans.svg';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MotionButton } from '@/components/ui/motion-button';
import { Separator } from '@/components/ui/separator';
import { slideInFromLeft, slideInFromRight, staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import { setPageMetadata } from '@/lib/utils';
import { createFileRoute, Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowRight, Award, Building2, ChevronRight, DollarSign, Goal, Home, Linkedin, PiggyBank, Sparkles, Users } from 'lucide-react';
import { useEffect } from 'react';

export const Route = createFileRoute('/_public/about')({
  component: AboutUsPage
})

function AboutUsPage() {
  useEffect(() => {
    setPageMetadata({
      title: 'About Swim Homes | Canada\'s AI-Powered Real Estate Platform',
      description: 'Meet the innovators behind Swim Homes, Canada\'s AI-powered real estate platform helping homeowners save $20,000+ when selling property independently. Our technology and values are transforming Canadian real estate.',
    });
  }, []);

  const teamMembers = [
    {
      name: 'Connor Kirby',
      role: 'Sales & Operations',
      image: connor_png,
      bio: "As Swim's business strategist, Connor combines his entrepreneurial mindset with a background in software engineering to revolutionize home selling. When he's not making waves in the pool, you'll find him crafting innovative real estate solutions that streamline transactions, making them as smooth as his freestyle stroke. Connor's competitive spirit and tech-savvy approach are changing the game for home sellers.",
      linkedin: 'https://www.linkedin.com/in/connorkirby1/',
    },
    {
      name: 'Andrey Karmanov',
      role: 'Tech Lead',
      image: andrey_png,
      bio: "As the architect behind Swim's cutting-edge tools, Andrey leverages his experience from Canada's top fintech companies to help Canadians save on real estate transactions. With a passion for technology and problem-solving, he creates user-friendly solutions that simplify the buying and selling process. Outside of work, Andrey stays active through sports, often drawing inspiration from teamwork in his innovative approach.",
      linkedin: 'https://linkedin.com/in/andrey-karmanov',
    },
  ];

  const companyValues = [
    {
      title: "Innovation",
      description: "We're constantly pushing boundaries to create tools that make real estate transactions simpler and more efficient.",
      icon: Sparkles
    },
    {
      title: "Transparency",
      description: "We believe in clear, honest communication and no hidden fees or surprises in our process.",
      icon: Users
    },
    {
      title: "Empowerment",
      description: "Our mission is to give homeowners the knowledge and tools to take control of their real estate journey.",
      icon: Goal
    },
    {
      title: "Excellence",
      description: "We're committed to delivering exceptional service and results for every client we work with.",
      icon: Building2
    }
  ];

  const benefits = [
    {
      title: "Save Thousands in Commission",
      icon: DollarSign,
    },
    {
      title: "Simplified Home Selling Experience",
      icon: Home,
    },
    {
      title: "Same Exposure as Using an Agent",
      icon: PiggyBank,
    },
    {
      title: "Cutting Edge Technology",
      icon: Award,
    },
  ];

  return (
    <>
      <div className="bg-white relative z-10 overflow-x-hidden">
        {/* Hero Section */}
        <section className="relative bg-linear-to-br from-[#0074b7] to-[#0057a7] overflow-hidden">
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 py-16 md:py-24 px-4 items-center">
            <div className="relative z-10">
              <span className="inline-block px-4 py-1 rounded-full bg-white/10 text-white text-sm font-medium mb-6">
                About Swim
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Reimagining <br />
                <span className="text-blue-200">Real Estate</span> <br />
                For Canadians
              </h1>
              <p className="text-lg md:text-xl text-blue-100 leading-relaxed opacity-90 mb-8 max-w-lg">
                We're on a mission to transform how homes are bought and sold, putting more money in homeowners' pockets.
              </p>
              <Button
                variant="secondary"
                size="lg"
                className="bg-white text-[#0074b7] hover:bg-white/90 font-medium rounded-full group"
                asChild
              >
                <Link to="/dashboard/edit">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>

            <div className="relative">
              <Card className="bg-white/10 backdrop-blur-sm border-none rounded-xl overflow-hidden shadow-xl max-w-md ml-auto">
                <CardContent className="p-6">
                  <ul className="space-y-4">
                    {benefits.map((benefit, index) => (
                      <motion.li
                        key={index}
                        className="flex items-center gap-3 text-white"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 + 0.3 }}
                      >
                        <div className="bg-white/20 rounded-full p-2 shrink-0">
                          <benefit.icon className="h-5 w-5 text-blue-100" />
                        </div>
                        <span className="font-medium">{benefit.title}</span>
                      </motion.li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <div className="mt-6 bg-white/5 backdrop-blur-sm p-4 rounded-xl border border-white/10 max-w-md ml-auto">
                <p className="text-blue-100 text-sm text-center italic">
                  "Swim helped us save over $15,000 in commission fees while providing exceptional service."
                </p>
                <p className="text-white/70 text-xs text-center mt-2">
                  — Satisfied Homeowner
                </p>
              </div>

              {/* Single decorative element instead of multiple */}
              <div className="absolute inset-0 -z-0 overflow-hidden">
                <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-blue-400/20 mix-blend-multiply filter blur-3xl"></div>
              </div>
            </div>
          </div>

          {/* Wave divider */}
          <div className="absolute -bottom-1 left-0 w-full overflow-hidden leading-[0]">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-[60px] md:h-[100px] text-white" style={{ display: 'block' }}>
              <path d="M0,0V30c180,60,420,-40,600,10c100,30,220,70,350,30c80,-25,170,-50,250,-10V120H0Z" fill="currentColor"></path>
            </svg>
          </div>
        </section>

        {/* Our Mission Section */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <motion.div
                className="md:w-2/5"
                variants={slideInFromLeft}
                initial="hidden"
                whileInView="visible"
                viewport={viewportOptions}
              >
                <div className="text-center md:text-left">
                  <div className="inline-flex items-center justify-center bg-[#0074b7]/5 rounded-2xl p-8 mb-6">
                    <img src={logo} alt="Swim Logo" className="h-36 md:h-48 w-auto" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="md:w-3/5"
                variants={slideInFromRight}
                initial="hidden"
                whileInView="visible"
                viewport={viewportOptions}
              >
                <div className="space-y-6">
                  <span className="inline-block px-4 py-1 rounded-full bg-blue-100 text-[#0074b7] text-sm font-medium">
                    Our Mission
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                    Help Canadians Keep More Equity
                  </h2>
                  <Separator className="w-24 h-1 bg-[#0074b7]" />
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Swim exists to help homeowners keep more of their equity when selling. Our AI tools, transparent approach, and straightforward pricing reduce commissions and make real estate work for Canadians, not against them.
                  </p>
                  <MotionButton
                    variant="outline"
                    size="lg"
                    className="mt-4 border-[#0074b7] text-[#0074b7] hover:bg-[#0074b7]/5 rounded-full"
                    asChild
                  >
                    <Link to="/sellers-guide">
                      Learn How We Do It
                      <ChevronRight className="ml-2 h-5 w-5" />
                    </Link>
                  </MotionButton>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-20 bg-gray-50 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOptions}
            >
              <motion.div
                variants={staggerItem}
                className="text-center mb-16"
              >
                <span className="inline-block px-4 py-1 rounded-full bg-blue-100 text-[#0074b7] text-sm font-medium mb-4">
                  Our Story
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Why We Started Swim
                </h2>
                <Separator className="w-24 h-1 bg-[#0074b7] mx-auto mt-6" />
              </motion.div>

              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    number: "1",
                    title: "The Problem",
                    description: "When we discovered that real estate fees are among the largest expenses Canadians face in their entire lives, we knew there had to be a better way. Swim was born from a simple yet powerful question: what if we could put those commission dollars back into homeowners' pockets?",
                    htmlDescription: "When we discovered that <strong>real estate fees are among the largest expenses Canadians face in their entire lives</strong>, we knew there had to be a better way. Swim was born from a simple yet powerful question: what if we could put those commission dollars back into homeowners' pockets?"
                  },
                  {
                    number: "2",
                    title: "Our Approach",
                    description: "Real estate transactions are complicated—there's no getting around that. We've focused on making the process clear and manageable by using AI and smart technology to handle the complex parts. This allows us to cut unnecessary costs and pass those savings directly to homeowners.",
                    htmlDescription: "Real estate transactions are complicated—there's no getting around that. We've focused on making the process clear and manageable by using AI and smart technology to handle the complex parts. This allows us to cut unnecessary costs and <strong>pass those savings directly to homeowners.</strong>"
                  },
                  {
                    number: "3",
                    title: "Looking Forward",
                    description: "We're in our early days, but every home listed with Swim helps us build a better real estate experience for Canadians. We take pride in truly listening to our customers and integrating their feedback to create a genuinely seamless home-selling journey.",
                    htmlDescription: "We're in our early days, but every home listed with Swim helps us build a better real estate experience for Canadians. <strong>We take pride in truly listening to our customers</strong> and integrating their feedback to create a genuinely seamless home-selling journey."
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    variants={staggerItem}
                  >
                    <Card className="bg-white border-none shadow-lg rounded-2xl overflow-hidden h-full hover:shadow-xl transition-shadow">
                      <CardContent className="p-8">
                        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-blue-100 mb-6">
                          <span className="text-[#0074b7] text-2xl font-bold">{item.number}</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                        <p
                          className="text-gray-700"
                          dangerouslySetInnerHTML={{ __html: item.htmlDescription }}
                        />
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Team Members Section */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOptions}
            >
              <motion.div
                variants={staggerItem}
                className="text-center mb-16"
              >
                <span className="inline-block px-4 py-1 rounded-full bg-blue-100 text-[#0074b7] text-sm font-medium mb-4">
                  Our Team
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Meet the Faces Behind Swim
                </h2>
                <Separator className="w-24 h-1 bg-[#0074b7] mx-auto mt-6" />
                <p className="text-lg text-gray-700 mt-6 max-w-2xl mx-auto">
                  Don't worry, we left the speedos at home for this photoshoot
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-12">
                {teamMembers.map((member, index) => (
                  <motion.div
                    key={index}
                    variants={staggerItem}
                  >
                    <Card className="rounded-3xl overflow-hidden border-none shadow-lg hover:shadow-xl transition-shadow h-full">
                      <CardContent className="p-0 h-full">
                        <div className="flex flex-col md:flex-row h-full">
                          <div className="md:w-2/5 relative">
                            <div className="absolute inset-0 bg-linear-to-br from-[#0074b7]/50 to-[#0057a7]/60 mix-blend-multiply z-10"></div>
                            <img
                              src={member.image}
                              alt={member.name}
                              className="w-full h-full object-cover aspect-square md:aspect-auto"
                            />
                            <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                              <h3 className="text-2xl font-bold text-white mb-1">{member.name}</h3>
                              <p className="text-lg text-blue-100">{member.role}</p>
                            </div>
                          </div>

                          <div className="md:w-3/5 p-8 flex flex-col">
                            <p className="text-gray-700 leading-relaxed mb-6 grow">{member.bio}</p>
                            {member.linkedin && (
                              <div>
                                <MotionButton
                                  variant="outline"
                                  size="sm"
                                  className="rounded-full"
                                  asChild
                                >
                                  <Link to={member.linkedin}>
                                    <Linkedin className="h-5 w-5 mr-2" />
                                    Connect on LinkedIn
                                  </Link>
                                </MotionButton>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Our Values Section */}
        <section className="py-20 bg-gray-50 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOptions}
            >
              <motion.div
                variants={staggerItem}
                className="text-center mb-16"
              >
                <span className="inline-block px-4 py-1 rounded-full bg-blue-100 text-[#0074b7] text-sm font-medium mb-4">
                  Core Values
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  What Drives Us
                </h2>
                <Separator className="w-24 h-1 bg-[#0074b7] mx-auto mt-6" />
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {companyValues.map((value, index) => (
                  <motion.div
                    key={index}
                    variants={staggerItem}
                  >
                    <motion.div
                      className="h-full"
                      whileHover={{ y: -8 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    >
                      <Card className="h-full border-none shadow hover:shadow-lg transition-all rounded-2xl overflow-hidden">
                        <CardContent className="p-8 h-full flex flex-col">
                          <div className="mb-6 rounded-2xl w-16 h-16 flex items-center justify-center bg-blue-100">
                            <value.icon className="h-8 w-8 text-[#0074b7]" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                          <p className="text-gray-700 grow">{value.description}</p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="relative py-20 bg-[#0074b7] text-white px-4">
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={viewportOptions}
              >
                <motion.span
                  variants={staggerItem}
                  className="inline-block px-4 py-1 rounded-full bg-white/10 text-white text-sm font-medium mb-6"
                >
                  Ready to Dive In?
                </motion.span>
                <motion.h2
                  variants={staggerItem}
                  className="text-3xl md:text-5xl font-bold mb-8"
                >
                  Start Your Home Selling Journey Today
                </motion.h2>
                <motion.p
                  variants={staggerItem}
                  className="text-xl opacity-90 leading-relaxed mb-10 max-w-3xl mx-auto"
                >
                  Experience a revolutionary approach to real estate with transparent pricing, advanced technology, and support right where you need it.
                </motion.p>

                <motion.div
                  variants={staggerItem}
                  className="flex justify-center"
                >
                  <MotionButton
                    variant="secondary"
                    size="lg"
                    className="bg-white text-[#0074b7] hover:bg-white/90 font-medium px-8 rounded-full"
                    asChild
                  >
                    <Link to="/dashboard/edit">
                      Get Started
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </MotionButton>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

